<template>
    <section class="sidebarFilter">
        <p class="sidebarFilter__desc">
            {{ trans('CONTENT_TYPE_TEXT') }}
        </p>
        <label
            v-for="(type, index) in contentFilterTypes"
            :key="index"
            class="sidebarFilter__wrapperCheckBox sidebarFilter__wrapperContentType">
            <span
                class="sidebarFilter__checkBox"
                :class="{ 'sidebarFilter__checkBox--disabled': type.disabledClass, 'sidebarFilter__checkBox--selected': type.selectedFilter }">
                <input
                    ref="checkbox"
                    v-model="selectedContentTypes"
                    :data-gtm="`sidebarFilter__contentType--option${ index }`"
                    type="checkbox"
                    class="customCheckboxInput"
                    :disabled="type.disabledClass"
                    :value="type.group"
                    :name="`sidebarFilter__contentType--option-${ index }`"
                    :multiple="true" />
                <span class="customCheckbox"> </span>
                <svg
                    width="24"
                    height="24"
                    aria-hidden="true"
                    class="sidebar__icon sidebar__icon--contentType">
                    <use :href="`${ type.icon }`" />
                </svg>
                {{ type.contentTypeName }}
            </span>
            <span class="sidebarFilter__pill">
                {{ type.count }}
            </span>
        </label>
    </section>
</template>

<script>
    import Translator from 'tools/trans';
    import { useActions, useGetters } from 'composables/useVuex';
    import { numberFormat } from 'tools/numberFormat';

    const GCS = 'gcs';
    const GCSREPORT = 'gcsReports';
    export default {
        name: 'ContentTypeFilter',
        data() {
            return {
                selectedContentTypes: this.getTabGroup,
                local: window.stGlobals.locale,
                contentFilterTypes: {
                    statisticPremium: {
                        contentTypeName: this.trans('FORM_SEARCH_STATISTICS'),
                        group: 'statistic',
                        entity: 'pureStatistic',
                        count: this.getCount('pureStatistic'),
                        icon: '#iconStatisticsSquare',
                        disabledClass: false,
                        selectedFilter: false,
                    },
                    studyBasis: {
                        contentTypeName: this.trans('FORM_SEARCH_REPORTS'),
                        group: 'report',
                        entity: 'report',
                        count: this.getCount('report'),
                        icon: '#iconStudiesSquare',
                        disabledClass: false,
                        selectedFilter: false,
                    },
                    topic: {
                        contentTypeName: this.trans('FORM_SEARCH_TOPICS'),
                        group: 'topic',
                        entity: 'topic',
                        count: this.getCount('topic'),
                        icon: '#iconTopicsSquare',
                        disabledClass: false,
                        selectedFilter: false,
                    },
                    xmo: {
                        contentTypeName: this.trans('MARKET_INSIGHTS'),
                        group: 'market',
                        entity: 'market',
                        count: this.getCount('market'),
                        icon: '#iconMarketInsightsSquare',
                        disabledClass: false,
                        selectedFilter: false,
                    },
                    forecast: {
                        contentTypeName: this.trans('FORECASTS_AND_SURVEYS'),
                        group: 'forecast',
                        entity: 'forecast',
                        count: this.getCount('forecast'),
                        icon: '#iconForecastsSquare',
                        disabledClass: false,
                        selectedFilter: false,
                    },
                    infographic: {
                        contentTypeName: this.trans('INFOGRAPHICS'),
                        group: 'infographic',
                        entity: 'infographic',
                        count: this.getCount('infographic'),
                        icon: '#iconInfographicsSquare',
                        disabledClass: false,
                        selectedFilter: false,
                    },
                    ecommercedb: {
                        contentTypeName: this.trans('E_COMMERCE_INSIGHTS'),
                        group: 'ecommercedb',
                        entity: 'ecommercedb',
                        count: ['en', 'de'].includes(window.stGlobals.locale) ? this.getCount('ecommercedb') : 'coming soon',
                        icon: '#iconEcommerceInsightsSquare',
                        disabledClass: ['en', 'de'].includes(window.stGlobals.locale) ? false : true,
                        selectedFilter: false,
                    },
                    gcs: {
                        contentTypeName: this.trans('CONSUMER_INSIGHTS'),
                        group: 'gcs',
                        entity: 'gcs',
                        count: ['en', 'de'].includes(window.stGlobals.locale) ? this.getCount('gcs') : 'coming soon',
                        icon: '#iconConsumerInsightsSquare',
                        disabledClass: ['en', 'de'].includes(window.stGlobals.locale) ? false : true,
                        selectedFilter: false,
                    },
                },
            };
        },
        watch: {
            selectedContentTypes: {
                handler(val) {
                    this.$emit('selected-contentTypes', val);
                    this.$parent.$emit('selected-contentTypes', val);
                },
                deep: true,
            },

        },
        emits: [
            'changeValue',
        ],
        methods: {
            trans: Translator.trans,
            getCount(name) {
                let count = this.getOriginalMetric(name);
                if (name === GCS) {
                    if (window.stGlobals.user && this.userPermissionToGCS) {
                        count = count + this.getOriginalMetric(GCSREPORT);
                    } else {
                        count = this.getOriginalMetric(GCSREPORT);
                    }
                }

                if (count > 1000) {
                    return `> ${ numberFormat(1000) }`;
                }
                if (isNaN(count)) {
                    return 0;
                }

                return Number(count);
            },
        },
        setup() {
            const getters = {
                ...useGetters('search', [
                    'query',
                    'resultCount',
                    'getMetric',
                    'getOriginalMetric',
                    'resultArray',
                    'masterTags',
                    'promotedArray',
                    'getTabGroup',
                    'repoArray',
                    'entity',
                    'entityName',
                    'getTitle',
                    'getContent',
                    'currentPage',
                    'totalFound',
                    'totalPages',
                    'loading',
                    'activeTab',
                    'topic',
                    'topics',
                    'countries',
                    'path',
                    'sort',
                    'focus',
                    'location',
                    'archive',
                    'tags',
                    'getShowSidebar',
                    'userPermissionToGCS',
                ]),
            };
            const actions = {
                ...useActions('search', [
                    'setActiveTab',
                    'startSearch',
                    'resetPageNumber',
                    'resetTagsPage',
                    'getPage',
                    'updateCurrentPage',
                    'updateLoaderStatus',
                    'setSort',
                    'setFocus',
                    'setFilters',
                    'setContentType',
                    'setValue',
                    'setArchive',
                    'setTags',
                    'unsetTags',
                    'resetAllTags',
                    'getReports',
                    'getTopic',
                    'updateShowSidebar',
                ]),
            };

            async function selectFilter(option) {
                await actions.resetPageNumber();
                actions.setContentType(option);
            }

            return {
                selectFilter,
                ...getters,
            };
        },
    };
</script>

